import { forwardRef, useCallback, useState } from 'react';
import { ProviderField } from '../../../../../../models/provider';
import { getPasswordInputIcon, PasswordInputIcon } from './PasswordInputIcon';
import { InputAutoCompleteType, InputEnterKeyHintType, TextField } from './TextField';

export type PasswordInputFieldProps = {
  value: string;
  disabled: boolean;
  errorMessage: string;
  providerField: ProviderField;
  showCheckIcon: boolean;
  hideCaption?: boolean;
  autoComplete?: string;
  enterKeyHint?: InputEnterKeyHintType;
  onChange: (event: React.FormEvent<HTMLInputElement>) => void;
};

export const PasswordInputField = forwardRef<HTMLInputElement, PasswordInputFieldProps>(
  (
    {
      value,
      disabled,
      errorMessage,
      providerField,
      showCheckIcon,
      autoComplete,
      hideCaption = false,
      enterKeyHint,
      onChange,
    },
    ref
  ) => {
    const [inputIcon, setInputIcon] = useState(
      showCheckIcon ? PasswordInputIcon.CHECK : PasswordInputIcon.NONE
    );

    const handleInputFocus = useCallback(() => {
      setInputIcon(PasswordInputIcon.CLOSED_EYE);
    }, []);

    const handleInputBlur = useCallback(() => {
      setInputIcon(showCheckIcon ? PasswordInputIcon.CHECK : PasswordInputIcon.NONE);
    }, [showCheckIcon]);

    const { name, description, hint, helpText } = providerField;

    return (
      <TextField
        forwardRef={ref}
        type={inputIcon === PasswordInputIcon.OPEN_EYE ? 'text' : 'password'}
        description={description}
        name={name}
        placeholder={hint || ''}
        value={value || ''}
        disabled={disabled}
        errorCaption={errorMessage}
        helpCaption={helpText ?? undefined}
        hideCaption={hideCaption}
        autoComplete={(autoComplete as InputAutoCompleteType) ?? 'off'}
        enterKeyHint={enterKeyHint}
        onChange={onChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        endIcon={getPasswordInputIcon(inputIcon, setInputIcon)}
        dataTestId="password-input-field"
      />
    );
  }
);
